import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import CustomNotify from '../../../Notify';
import PostbackParametersComponent from '../PostbackParameters/PostbackParameters.container';
import PostbackGoalActionsComponent from '../PostbackGoalActions/PostbackGoalActions.container';
import PostbackTestComponent from '../PostbackTest/PostbackTest.container';
import FormGroup from '../../../FormGroup';
import ConversionStatuses from '../ConversionStatuses';
import ViewLink from '../ViewLink';
import TrackParameters from '../TrackParameters';
import SuccessString from '../SuccessString';
import config from '../../../../config';
import ButtonsList from '../../../ButtonsList';
import { I18nContext } from 'utils/I18n/context';

const PostbackPage = ({
    postback,
    currencies,
    locales,
    goalActionIds,
    errors,
    loadingSave,
    params,
    user,
    onSave,
    clearErrors
}) => {
    const { t } = useContext(I18nContext);
    const [state, setState] = useState({
        id: params.id || null,
        edit: !!params.id,
        title: postback.title,
        url: postback.url,
        queryParams: [],
        method: postback.method,
        trackParameter: postback.track_parameter || {},
        successString: postback.success_string || '',
        conversionStatuses: params.id ? (postback.conversion_statuses || []) : ['confirmed'],
        currency: postback.currency || user.publisher.account.balance.currency,
        locale: postback.locale || user.locale,
        errors: errors || {},
        loadingSave: loadingSave || false,
        goalActionIds: postback.goal_action_ids || []
    });

    const staticNames = t?.postbackPage?.creatingEditing || {};

    const onShowError = useCallback(() => {
        CustomNotify(staticNames.commonError, 'error');
    }, [staticNames.commonError]);

    const onChangeField = useCallback((event) => {
        const name = event.target.name;
        let value = event.target.value;

        setState(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    const onChangeDropdownList = useCallback((item, type) => {
        setState(prev => ({
            ...prev,
            [type]: item.value
        }));
    }, []);

    const onParametersAction = useCallback((state) => {
        setState(prev => ({
            ...prev,
            queryParams: state.selected
        }));
    }, []);

    const onGoalAction = useCallback((ids) => {
        setState(prev => ({
            ...prev,
            goalActionIds: ids
        }));
    }, []);

    const onConversionStatusesAction = useCallback((state) => {
        setState(prev => ({
            ...prev,
            conversionStatuses: state.statuses
        }));
    }, []);

    const onTrackParameterAction = useCallback((state) => {
        setState(prev => ({
            ...prev,
            trackParameter: {
                ...state
            }
        }));
    }, []);

    const onSuccessString = useCallback((text) => {
        setState(prev => ({
            ...prev,
            successString: text
        }));
    }, []);

    const onSubmit = useCallback((event) => {
        event.preventDefault();
        onSave(state);
    }, [state, onSave]);

    const getListFields = useCallback((list, name, curValue) => {
        return list.map((item) => (
            <label className="checkbox-inline i-checks option" key={item.value}>
                <input
                    className="option__input"
                    type="radio"
                    name={name}
                    value={item.value}
                    onChange={onChangeField}
                    checked={curValue === item.value}
                />
                <span className="option__mark" />
                <span className="option__caption">{item.title}</span>
            </label>
        ));
    }, [onChangeField]);

    const getForm = useCallback(() => {
        return (
            <div className="top-form">
                <FormGroup
                    fieldClass="input-wrapper"
                    errors={state.errors.title}
                >
                    <label className="bold"> 
                        <div> {staticNames.form?.name}* </div>
                        <input
                            className="form-control"
                            type="text"
                            name="title"
                            value={state.title}
                            onChange={onChangeField}
                            required={true}
                        />
                    </label>
                </FormGroup>
                <FormGroup
                    fieldClass="input-wrapper"
                    errors={state.errors.url}
                >
                     <label className="bold"> 
                        <div> {staticNames.form?.url}* </div>
                        <input
                            className="form-control"
                            type="text"
                            name="url"
                            value={state.url}
                            onChange={onChangeField}
                            required={true}
                        />
                    </label>
                </FormGroup>
                <FormGroup
                    fieldClass="input-wrapper"
                    errors={state.errors.currency}
                >
                    <ButtonsList 
                        fields={[{key: 'currencies', list: currencies, label: staticNames.form?.currency}]} 
                        data={{key: 'currencies'}} 
                        currentValue={state.currency}
                    />
                </FormGroup>
                <FormGroup
                    fieldClass="input-wrapper"
                    errors={state.errors.locale}
                >
                    <ButtonsList 
                        fields={[{key: 'locale', list: locales, label: staticNames.form?.locale}]} 
                        data={{key: 'locale'}} 
                        currentValue={state.locale}
                    />
                </FormGroup>
                <FormGroup
                    fieldClass="input-wrapper"
                    errors={state.errors.method}
                >
                    <label className="bold"> 
                        {staticNames.form?.method}
                        {getListFields([
                            {value: 'GET', title: 'GET'},
                            {value: 'POST', title: 'POST'}
                        ], 'method', state.method)}
                    </label>
                </FormGroup>
                <FormGroup
                    fieldClass="input-wrapper"
                    errors={state.errors.successString}
                >
                    <label className="bold">
                        {staticNames.form?.successString}
                        <SuccessString
                            text={state.successString}
                            onAction={onSuccessString}
                        />
                    </label>
                </FormGroup>
            </div>
        );
    }, [state, staticNames, currencies, locales, onChangeField, getListFields, onSuccessString]);

    useEffect(() => {
        setState(prev => ({
            ...prev,
            errors: errors || {},
            loadingSave: loadingSave || false
        }));

        if (Object.keys(errors || {}).length) {
            onShowError();
        }
    }, [errors, loadingSave, onShowError]);

    useEffect(() => {
        return () => {
            if (Object.keys(state.errors).length && clearErrors) {
                clearErrors();
            }
        };
    }, [state.errors, clearErrors]);

    return (
        <div className="ibox-content">
            <div className="postback__add-block">
                <form className="postback__add-left" onSubmit={onSubmit}>
                    <h2 className="bold">{staticNames.newConversionPostback}</h2>
                    {getForm()}
                    <h2 className="bold">{staticNames.filter}</h2>
                     <div className="postback__filter">
                        <TrackParameters
                            data={state.trackParameter}
                            onAction={onTrackParameterAction}
                        />
                        <ConversionStatuses
                            onAction={onConversionStatusesAction}
                            type={state.edit}
                            statuses={state.conversionStatuses}
                        />

                        <PostbackGoalActionsComponent
                            onAction={onGoalAction}
                        />
                    </div>
                    <PostbackParametersComponent
                        onAction={onParametersAction}
                        errors={state.errors.parameters}
                    />
                    <ViewLink
                        urlPostback={state.url}
                        gethParams={state.queryParams}
                    />
                    <PostbackTestComponent
                        edit={state.edit}
                        id={state.id}
                    />
                </form>
            </div>
        </div>
    );
};

PostbackPage.propTypes = {
    postback: PropTypes.shape({
        conversion_statuses: PropTypes.array,
        currency: PropTypes.string,
        goal_action_ids: PropTypes.array,
        locale: PropTypes.string,
        method: PropTypes.string,
        parameters: PropTypes.array,
        title: PropTypes.string,
        url: PropTypes.string
    }),
    currencies: PropTypes.array,
    locales: PropTypes.array,
    goalActionIds: PropTypes.array,
    errors: PropTypes.object,
    loadingSave: PropTypes.bool,
    params: PropTypes.shape({
        id: PropTypes.string
    }),
    user: PropTypes.shape({
        publisher: PropTypes.shape({
            account: PropTypes.shape({
                balance: PropTypes.shape({
                    currency: PropTypes.string
                })
            })
        }),
        locale: PropTypes.string
    }),
    onSave: PropTypes.func,
    clearErrors: PropTypes.func
};

PostbackPage.defaultProps = {
    postback: {
        conversion_statuses: [],
        currency: '',
        goal_action_ids: [],
        locale: '',
        method: 'GET',
        parameters: [],
        title: '',
        url: ''
    },
    locales: config.locales,
    goalActionIds: [],
    errors: {},
    loadingSave: false,
    params: {},
    user: {
        publisher: {
            account: {
                balance: {
                    currency: ''
                }
            }
        },
        locale: ''
    },
    onSave: () => {},
    clearErrors: () => {}
};

export default PostbackPage;
